<template>
  <div class="modal-overlay">
    <div class="modal-content-wrapper">
      <div class="modal-header">
        Create Purchase Order Item
        <CloseSvg
          class="close"
          :dark="true"
          @click="onCloseClicked"
        />
      </div>
      <div class="modal-content">
        <TextInput
          v-if="inventoryItem"
          v-model="inventoryItem.name"
          :disabled="true"
          label="Inventory Item"
        />
        <NumericInput
          v-if="inventoryItem"
          v-model="useForFulfillmentQuantity"
          label="Use For Fulfillment Quantity"
          class="input"
        />
        <NumericInput
          v-if="inventoryItem"
          v-model="quantity"
          label="Quantity"
          class="input"
        />
        <button
          v-if="!inventoryItem"
          @click="showSelectPartModal = true"
        >
          Choose Part
        </button>
      </div>
      <div class="modal-footer">
        <LockingButton
          class="save-button"
          :disabled="!isFormValid"
          :locked="saving"
          :text="'Save'"
          @click="onSaveClicked"
        />
      </div>
    </div>
    <SelectPartModal 
      v-if="showSelectPartModal"
      :supplier-id="supplierId"
      @close="showSelectPartModal = false"
      @inventory-item="onInventoryItemSelected"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import LockingButton from '@/components/shared/LockingButton.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { InventoryItemDetails } from '@/@types/responses/inventory_item/InventoryItemDetails'
import { InventoryItem } from '@/@types/responses/inventory_item/InventoryItem'
import SelectPartModal from '@/components/modals/SelectPartModal.vue'
import { PurchaseOrderService } from '@/services/purchaseOrder.service'
import { PurchaseOrderItemService } from '@/services/purchaseOrderItem.service'
import { CreatePurchaseOrderItemMessage } from '@/@types/messages/CreatePurchaseOrderItemMessage'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'CreatePurchaseOrderItemModal',
  components: {
    CloseSvg,
    LockingButton,
    NumericInput,
    SelectPartModal,
    TextInput
  },
  props: {
    purchaseOrderId: {
      type: Number,
      required: true
    },
    supplierId: {
      type: Number,
      required: true
    }
  },
  emits: ['purchase-order-item-created', 'close'],
  setup() {
    const purchaseOrderItemService = inject(PROVIDER_CONSTANTS.purchaseOrderItemService) as PurchaseOrderItemService
    const purchaseOrderService = inject(PROVIDER_CONSTANTS.purchaseOrderService) as PurchaseOrderService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      purchaseOrderItemService,
      purchaseOrderService,
      toast
    }
  },
  data() {
    let inventoryItem: InventoryItemDetails|undefined

    return {
      inventoryItemId: 0,
      inventoryItem,
      quantity: 1,
      saving: false,
      showSelectPartModal: false,
      useForFulfillmentQuantity: 0
    }
  },
  computed: {
    isFormValid(): boolean {
      return this.inventoryItemId !== 0 &&
             this.quantity > 0 &&
             this.useForFulfillmentQuantity >= 0 &&
             this.quantity >= this.useForFulfillmentQuantity
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onInventoryItemSelected(inventoryItem: InventoryItem): void {
      this.inventoryItemId = inventoryItem.id
      this.inventoryItem = new InventoryItemDetails(inventoryItem.id, inventoryItem.name)
      this.showSelectPartModal = false
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const createPurchaseOrderItemMessage = new CreatePurchaseOrderItemMessage(this.inventoryItemId, this.quantity, this.useForFulfillmentQuantity)
      this.purchaseOrderService.createPurchaseOrderItem(this.purchaseOrderId, createPurchaseOrderItemMessage).then(() => {
        this.$emit('purchase-order-item-created')
        this.toast.success('Purchase order item created successfully.')
        this.$emit('close')
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to create the purchase order item. Please try again.')
        }
      })
    }
  }
})
</script>

<style scoped lang="scss">
.modal-overlay {
  @include modal-styles;
}

.modal-overlay > .modal-content-wrapper {
  > .modal-content {
    > .input {
      margin-top: 15px;
    }

    > button {
      @include secondary-button;
    }
  }
}
</style>